import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, FileText, Mail, Megaphone, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Authors = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen pt-16 bg-hermes-400">
      {/* Logo fixe en haut à gauche */}
      <div className="fixed top-24 left-8 z-40">
        <img 
          src="/logo-quid-novi.png" 
          alt="Quid Novi" 
          className="h-20 w-20"
        />
      </div>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6">
            {t.authors.title}
          </h1>
          <p className="text-xl text-white/90 font-medium mb-4">
            {t.authors.subtitle}
          </p>
          <p className="text-lg text-white/80 leading-relaxed">
            {t.authors.intro}
          </p>
        </div>
      </section>

      {/* Publishing Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-white mb-4">
              {t.authors.process.title}
            </h2>
            <div className="w-24 h-1 bg-white mx-auto rounded-full"></div>
          </div>

          <div className="space-y-8">
            {t.authors.process.steps.map((step, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row gap-6 items-start bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20 hover:bg-white/20 transition-colors"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-hermes-600 text-white rounded-xl flex items-center justify-center text-2xl font-bold">
                    {step.number}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-semibold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-white/80 leading-relaxed text-lg">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/submission"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-hermes-600 text-lg font-medium rounded-lg hover:bg-hermes-50 transition-all hover:scale-105 shadow-lg"
            >
              <span>{t.authors.cta}</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Requirements Accordion */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-serif font-bold text-white mb-4">
              Modalités de soumission
            </h2>
            <p className="text-lg text-white/80">
              Tout ce que vous devez savoir pour préparer votre manuscrit
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {/* Manuscript Requirements */}
            <AccordionItem value="requirements" className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline py-6">
                <div className="flex items-center space-x-3">
                  <FileText className="w-6 h-6 text-white" />
                  <span className="text-xl font-semibold text-white">
                    {t.authors.requirements.title}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="space-y-6 pt-4">
                  <div>
                    <h4 className="font-semibold text-white mb-3 text-lg">
                      {t.authors.requirements.format.title}
                    </h4>
                    <ul className="space-y-2">
                      {t.authors.requirements.format.items.map((item, index) => (
                        <li key={index} className="flex items-start space-x-2 text-white/80">
                          <CheckCircle className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-3 text-lg">
                      {t.authors.requirements.structure.title}
                    </h4>
                    <ul className="space-y-2">
                      {t.authors.requirements.structure.items.map((item, index) => (
                        <li key={index} className="flex items-start space-x-2 text-white/80">
                          <CheckCircle className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Sales Sheet */}
            <AccordionItem value="argumentaire" className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline py-6">
                <div className="flex items-center space-x-3">
                  <Mail className="w-6 h-6 text-white" />
                  <span className="text-xl font-semibold text-white">
                    {t.authors.argumentaire.title}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="pt-4">
                  <p className="text-white/80 mb-4 leading-relaxed">
                    {t.authors.argumentaire.description}
                  </p>
                  <ul className="space-y-2">
                    {t.authors.argumentaire.items.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2 text-white/80">
                        <CheckCircle className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Communication */}
            <AccordionItem value="communication" className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-6">
              <AccordionTrigger className="text-left hover:no-underline py-6">
                <div className="flex items-center space-x-3">
                  <Megaphone className="w-6 h-6 text-white" />
                  <span className="text-xl font-semibold text-white">
                    {t.authors.communication.title}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="pt-4">
                  <p className="text-white/80 mb-4 leading-relaxed">
                    {t.authors.communication.description}
                  </p>
                  <ul className="space-y-2">
                    {t.authors.communication.items.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2 text-white/80">
                        <CheckCircle className="w-5 h-5 text-white mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-hermes-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-serif font-bold mb-6 text-white">
            Prêt à nous soumettre votre manuscrit ?
          </h2>
          <p className="text-xl text-hermes-100 mb-10 leading-relaxed">
            Notre équipe évaluera votre projet avec attention et vous contactera rapidement.
          </p>
          <Link
            to="/submission"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-hermes-600 text-lg font-medium rounded-lg hover:bg-neutral-100 transition-all hover:scale-105 shadow-lg"
          >
            <span>{t.authors.cta}</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Authors;
